<template>
  <!-- Cropper Component: Allows users to crop, rotate, zoom, and optionally change the background color of an image -->
  <div class="cropper">
    <v-card class="d-flex flex-column align-center justify-center">
      <div class="pa-5" style="width: 100%">
        <div
            class="cropper-container"
            :style="`background-color: ${color}`"
        >
          <vue-cropper
              v-if="src"
              ref="cropper"
              :src="src"
              :aspect-ratio="aspectRatio"
              :auto-crop-area="1"
              :zoom-on-wheel="false"
              :background="true"
              :highlight="false"
              :modal="false"
              :responsive="true"

          ></vue-cropper>
        </div>
      </div>
      <!-- Actions: Includes buttons for confirming crop, rotating, zooming, and toggling the color picker -->
      <v-card-actions style="position: relative">
        <v-btn icon @click="confirmCrop">
          <v-icon>mdi-check</v-icon>
        </v-btn>
        <v-btn icon @click="rotateRight">
          <v-icon>mdi-rotate-right</v-icon>
        </v-btn>
        <v-btn icon @click="rotateLeft">
          <v-icon>mdi-rotate-left</v-icon>
        </v-btn>
        <v-btn icon @click="zoomIn">
          <v-icon>mdi-magnify-plus-outline</v-icon> </v-btn
        ><v-btn icon @click="zoomOut">
        <v-icon>mdi-magnify-minus-outline</v-icon>
      </v-btn>
        <v-btn

            :style="{ backgroundColor: openColorPicker ? `#e0e0e0` : null }"
            icon
            @click="openColorPicker = !openColorPicker"
        >
          <v-icon>mdi-format-color-fill</v-icon>
        </v-btn>
        <v-btn  icon @click="$emit('closeUpload')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-color-picker
            v-model="color"
            dot-size="25"
            swatches-max-height="200"
            v-if="openColorPicker"
            style="
            position: absolute;
            bottom: 50px;
            left: 50%;
            transform: translateX(-50%);
            z-index: 9999;
          "
        ></v-color-picker>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import VueCropper from 'vue-cropperjs'
import 'cropperjs/dist/cropper.css'
import imageCompression from "browser-image-compression";

/**
 * Cropper Component
 *
 * This component provides an interactive interface for cropping images with additional
 * functionalities such as zooming, rotating, and setting a background fill color. It also
 * compresses the cropped image if needed.
 *
 * Dependencies:
 * - VueCropper (vue-cropperjs): Wrapper for the Cropper.js library
 * - Cropper.js: Handles image cropping
 * - Browser Image Compression: Compresses large images to reduce file size
 *
 * Props:
 * - src (String): Base64 or URL of the image to crop (required).
 * - aspectRatio (Number): Aspect ratio for the cropping area (optional).
 * - fileType (String): File type for the cropped image (default: "image/jpeg").
 * - width (Number): Desired width of the cropped output.
 * - height (Number): Desired height of the cropped output.
 */

export default {
  components: {
    VueCropper,
  },
  data() {
    return {
      color: '#00000000',
      openColorPicker: false,
    }
  },
  props: {
    /**
     * base64 image url
     */
    src: String,
    aspectRatio: Number,
    /**
     * the image file type for crroped image
     * @default "image/jpeg"
     */
    fileType: {type: String, default: "image/jpeg"},
    width: Number,
    height: Number

  },
  methods: {
    zoomIn() {
      this.$refs.cropper.relativeZoom(0.1)
    },
    zoomOut() {
      this.$refs.cropper.relativeZoom(-0.1)
    },
    rotateLeft() {
      this.$refs.cropper.rotate(-90)
    },
    rotateRight() {
      this.$refs.cropper.rotate(90)
    },
    async confirmCrop() {
      const cropper = this.$refs.cropper
      const canvasData = cropper.getCroppedCanvas({
        width: this.width,
        height: this.height,
        fillColor: this.color ?? undefined,
      })
      // const mimeType = this.media.url.split(':')[1].split(';')[0]
      const croppedBlob = await new Promise(resolve => {
        canvasData.toBlob(blob => {
          resolve(blob)
        }, this.fileType, 1.0)
      })
      const compressedAndCroppedImage = await this.compressImage(croppedBlob)
      // Emit the cropped data to the parent component
      this.$emit('crop-confirmed', {
        file: compressedAndCroppedImage,
        url: canvasData.toDataURL(),
        mime: 'image/jpeg',
        fillColor:  this.color ?? false
      })
      this.cropperOpen = false
    },
    async compressImage(imageFile) {
      console.log('originalFile instanceof Blob', imageFile instanceof Blob); // true
      console.log(`originalFile size ${imageFile.size / 1024 / 1024} MB`);
      // dont compress image smaller than 1 mb
      if (imageFile.size / 1024 < 1024)
        return imageFile;
      const options = {
        maxSizeMB: 600,
        useWebWorker: true,
      }
      try {
        const compressedFile = await imageCompression(imageFile, options);
        console.log('compressedFile instanceof Blob', compressedFile instanceof Blob); // true
        console.log(`compressedFile size ${compressedFile.size / 1024 / 1024} MB`); // smaller than maxSizeMB
        return compressedFile;
      } catch (error) {
        console.log(error);
      }

    },
    onEnter(event) {
      if (event.key === 'Enter') {
        this.confirmCrop()
      }
    },
  },
  mounted() {
    this.$emit('cropper-mounted', this.$refs.cropper)
    window.addEventListener('keyup', this.onEnter)
  },
  beforeDestroy() {
    window.removeEventListener('keyup', this.onEnter)
  },
}
</script>

<style lang="scss">
/* Your original component styles */
.cropper {
  border: 3px solid transparent; /*2*/
  background: linear-gradient(#ffffff 0 0) padding-box,
  var(--linearHotPinkDodgerBlue) border-box;
  border-radius: 13px;
  overflow: hidden;
  .hide {
    display: none !important;
  }

  .cropped-image-container img {
    max-width: 100%;
    margin-top: 20px;
    border: 1px solid #ccc;
  }
}
</style>
